import moment from 'moment';
import { useSelector } from 'react-redux';

import { ReadMore } from '@/components/modules/readmore';
import { RatingV2 } from '@/components/elements/redesign/RatingV2/RatingV2';
import { _t, getLang } from '@/locale';
import { Review } from '@/types/reviews';
import { Button } from '@/components/elements/forms/buttons';
import { capitalizeFirstLetter, classnames } from '@/helpers';

const baseTranslationKey = 'components.elements.Card.ReviewCardV2';

type ReviewCardProps = {
  reviewId: string;
  onEmployeeClick?: (employeeId: number, serviceId: number) => void;
  onServiceClick?: (employeeId: number, serviceId: number) => void;
};

export const ReviewCardV2 = ({ reviewId, onEmployeeClick, onServiceClick }: ReviewCardProps) => {
  const review = useSelector((state: { reviews: { all: Record<string, Review> } }) => state.reviews.all[reviewId]);

  const { author, employee, service: rawService, reply } = review;
  const { score, text } = review.review;
  const service = typeof rawService === 'object' ? rawService : undefined;

  return (
    <div className={classnames('bg-bg_secondary p-xl gap-md flex w-full flex-col rounded-lg')}>
      <div className="gap-md flex items-start justify-between">
        <span className="text-fg_primary text-lg font-bold">{author?.name || _t('Anonymous')}</span>
        <RatingV2 averageRating={score} size="md" />
      </div>
      <div className="gap-md flex items-center justify-between">
        {employee?.name && (
          <span className="text-m text-fg_secondary">
            <span className="capitalize">{_t('to')}</span>{' '}
            {onEmployeeClick ? (
              <Button
                inline
                size={'sm'}
                variant={'link'}
                className={'text-left'}
                onClick={() => {
                  onEmployeeClick?.(employee.id, service?.id);
                }}>
                {employee.name}
              </Button>
            ) : (
              <span className="text-m text-fg_secondary">{employee.name}</span>
            )}
            {service?.name && (
              <>
                {` ${_t(`${baseTranslationKey}.for`)} `}
                {onServiceClick && service.active ? (
                  <Button
                    inline
                    size={'sm'}
                    variant={'link'}
                    className={'text-left'}
                    onClick={() => {
                      onServiceClick?.(employee.id, service.id);
                    }}>
                    {service.name}
                  </Button>
                ) : (
                  <span className="text-m text-fg_secondary">{service.name}</span>
                )}
              </>
            )}
          </span>
        )}
        {review.createdAt ? (
          <>
            <span className="text-fg_secondary text-s">
              {capitalizeFirstLetter(moment(review.createdAt).locale(getLang()).fromNow())}
            </span>
          </>
        ) : null}
      </div>
      {text && (
        <ReadMore
          maxLines={3}
          text={text}
          className="text-fg_primary text-m"
          {...(reply?.text
            ? {
                bottomSlot: (
                  <div className="gap-md flex">
                    <div className="ml-md">
                      <svg width="20" height="57" viewBox="0 0 20 57" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M2 2V39C2 47.8366 9.16344 55 18 55V55"
                          stroke={'#B8C3CC'}
                          strokeWidth={3}
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <div className="bg-surface_l3 p-xl gap-xs relative flex flex-1 flex-col rounded-lg">
                      <div className="gap-xs flex items-center justify-between">
                        <span className="text-m text-fg_primary font-bold">
                          {_t(`${baseTranslationKey}.companyReplyTitle`)}
                        </span>
                        {reply?.createdAt && (
                          <>
                            <span className="text-fg_secondary text-s">
                              {capitalizeFirstLetter(moment(reply?.createdAt).locale(getLang()).fromNow())}
                            </span>
                          </>
                        )}
                      </div>
                      <p className="text-m text-fg_primary">{reply.text}</p>
                    </div>
                  </div>
                ),
              }
            : {})}
        />
      )}
    </div>
  );
};

export default ReviewCardV2;
